/**
 * Canvas App Component
 *
 * Wrapper component that detects Salesforce Canvas environment
 * and initializes the Product Discovery application accordingly.
 *
 * When running inside Canvas:
 * - Processes Signed Request
 * - Establishes server-side session
 * - Displays Quote/Order context
 * - Switches to live ARM API mode
 *
 * When running standalone:
 * - Uses mock data
 * - Shows development mode indicator
 */
import React, { useEffect, useState } from 'react';
import { useCanvas, LaunchContext } from '../hooks/useCanvas';
import { ProductDiscovery } from './ProductDiscovery';

/**
 * Loading indicator
 */
function LoadingState() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <h2 className="text-xl font-semibold text-gray-900">Loading Product Discovery...</h2>
        <p className="text-gray-600 mt-2">Connecting to Salesforce</p>
      </div>
    </div>
  );
}

/**
 * Error state with retry
 */
interface ErrorStateProps {
  error: string;
  onRetry: () => void;
}

function ErrorState({ error, onRetry }: ErrorStateProps) {
  const [isMock, setIsMock] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center">
          <div className="bg-red-100 rounded-full h-16 w-16 flex items-center justify-center mx-auto mb-4">
            <svg
              className="h-8 w-8 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Unable to Load Product Discovery
          </h2>

          <p className="text-gray-600 mb-6">{error}</p>

          <div className="space-y-3">
            <button
              onClick={onRetry}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md font-medium hover:bg-blue-700 transition-colors"
            >
              Retry Connection
            </button>

            <button
              onClick={() => setIsMock(true)}
              className="w-full bg-gray-200 text-gray-700 px-4 py-2 rounded-md font-medium hover:bg-gray-300 transition-colors"
            >
              Continue with Mock Data
            </button>
          </div>
        </div>
      </div>

      {isMock && <MockModeIndicator />}
    </div>
  );
}

/**
 * Launch context display
 */
interface LaunchContextDisplayProps {
  context: LaunchContext;
}

function LaunchContextDisplay({ context }: LaunchContextDisplayProps) {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-blue-900">Salesforce Context</h3>
          <p className="text-xs text-blue-700 mt-1">
            {context.transactionType} {context.transactionId}
          </p>
          {context.currencyCode && (
            <p className="text-xs text-blue-600 mt-1">Currency: {context.currencyCode}</p>
          )}
        </div>
        <div className="flex items-center text-green-600">
          <svg className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-sm font-medium">Connected</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Mock mode indicator
 */
function MockModeIndicator() {
  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 m-4">
      <div className="flex items-center">
        <svg
          className="h-5 w-5 text-yellow-600 mr-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span className="text-sm font-medium text-yellow-800">
          Development Mode - Using Mock Data
        </span>
      </div>
      <p className="text-xs text-yellow-700 mt-1">
        You are viewing sample data. Launch from Salesforce Quote/Order to see live data.
      </p>
    </div>
  );
}

/**
 * Main Canvas App component
 */
export function CanvasApp(): React.ReactElement {
  const { isInCanvas, launchContext, isLoading, error, hasSession, refreshSession } = useCanvas();
  const [showMock, setShowMock] = useState(false);

  // Allow escape to mock mode for development
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape key forces mock mode
      if (e.key === 'Escape' && !showMock) {
        console.log('[CanvasApp] Escape pressed - switching to mock mode');
        setShowMock(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showMock]);

  // Show loading while initializing
  if (isLoading) {
    return <LoadingState />;
  }

  // Show error state with retry option
  if (error && !hasSession && !showMock) {
    return <ErrorState error={error} onRetry={refreshSession} />;
  }

  // Determine if we should use live mode or mock mode
  const useLiveMode = hasSession && !showMock;
  const showCanvasContext = isInCanvas && launchContext && !showMock;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Context header (only in Canvas) */}
      {showCanvasContext && <LaunchContextDisplay context={launchContext} />}

      {/* Mock mode indicator (when not in Canvas or forced mock) */}
      {(!isInCanvas || showMock) && <MockModeIndicator />}

      {/* Main Product Discovery component */}
      <ProductDiscovery
        mode={useLiveMode ? 'live' : 'mock'}
        launchContext={launchContext || undefined}
      />
    </div>
  );
}

export default CanvasApp;
